import { Link } from "react-router-dom";
import { CalendarDays, Clock, Stethoscope, CheckCircle2, FlaskConical, HeartPulse, AlertTriangle, Info } from "lucide-react";
import { Kpi, PageHeader, Panel, RowsSkeleton } from "@/components/app/kit";
import { MedStatus } from "@/components/med/shared";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";

export default function TenantDashboard() {
  const { me } = useAuth();
  const { data, loading } = useFetch("/t/dashboard");
  if (loading || !data) return <RowsSkeleton rows={6} />;
  const kpis = [
    data.appointments_today && { label: "Rendez-vous du jour", value: data.appointments_today.length, icon: CalendarDays, testid: "kpi-appointments-today" },
    data.waiting && { label: "Patients en attente", value: data.waiting.length, icon: Clock, tone: "amber", testid: "kpi-waiting" },
    data.consultations_in_progress != null && { label: "Consultations en cours", value: data.consultations_in_progress, icon: Stethoscope, tone: "indigo", testid: "kpi-consult-progress" },
    data.consultations_done_today != null && { label: "Consultations terminées (jour)", value: data.consultations_done_today, icon: CheckCircle2, tone: "green", testid: "kpi-consult-done" },
    data.exams_pending != null && { label: "Examens en attente", value: data.exams_pending, icon: FlaskConical, tone: "amber", testid: "kpi-exams-pending" },
    data.patients != null && { label: "Patients", value: data.patients, icon: HeartPulse, tone: "green", testid: "kpi-patients" },
  ].filter(Boolean);
  return (
    <div>
      <PageHeader eyebrow={me.tenant.name} title={`Bonjour ${me.user.name?.split(" ")[0] || ""}`} subtitle={`Tableau de bord — ${me.role?.name}. Seuls les indicateurs autorisés pour votre rôle sont affichés.`} />
      <div className="space-y-6 animate-fade-up">
        {data.alerts.length > 0 && (
          <div className="space-y-2" data-testid="dashboard-alerts">
            {data.alerts.map((a, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-sm ${a.level === "warning" ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-sky-50 border-sky-200 text-sky-800"}`}>
                {a.level === "warning" ? <AlertTriangle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
                {a.to ? <Link to={a.to} className="hover:underline">{a.text}</Link> : a.text}
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">{kpis.map((k) => <Kpi key={k.testid} {...k} />)}</div>
        {data.appointments_today && (
          <Panel title="Rendez-vous du jour" actions={<Link to="/cabinet/agenda" className="text-sm text-sky-700 hover:underline">Ouvrir l'agenda</Link>}>
            {data.appointments_today.length === 0 ? <p className="text-sm text-slate-500">Aucun rendez-vous aujourd'hui.</p> : (
              <ul className="divide-y divide-slate-100 -my-2" data-testid="today-appointments">
                {data.appointments_today.map((a) => (
                  <li key={a.id} className="py-2.5 flex items-center gap-4 text-sm">
                    <span className="font-mono text-xs w-12">{a.start.slice(11)}</span>
                    <span className="font-medium flex-1">{a.patient_name}</span>
                    <span className="text-slate-500 hidden sm:block">{a.reason}</span>
                    <MedStatus s={a.status} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        )}
      </div>
    </div>
  );
}
